import React, { useEffect, useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import PDFViewer from './PdfViewer';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime'; // Add this import
import 'dayjs/locale/sv'; // Add Swedish locale if you want Swedish relative time

import {
  Spinner,
  Button,
  Badge,
  Alert,
  Modal,
  Dropdown,
  ButtonGroup,
} from 'react-bootstrap';

import { getApiUrl } from '../../../../utils/ApiUtils';
import PageTitle from '../../../layouts/PageTitle';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faPaperPlane,
  faCheckCircle,
  faTimesCircle,
  faBan,
  faArrowLeft,
  faDownload,
  faPrint,
  faEdit,
  faCopy,
  faEuroSign,
  faCalendarAlt,
  faClock,
  faUser,
  faBuilding,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faFileInvoice,
  faEllipsisV,
  faEye,
  faExclamationTriangle,
  faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';

dayjs.extend(relativeTime);
dayjs.locale('sv');

const QuoteViewer = () => {
  const { companyId, quoteId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // State for quote data
  const [quoteData, setQuoteData] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [nextAction, setNextAction] = useState(null);

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [accessStats, setAccessStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    fetchQuote();
    fetchPdf();
  }, [companyId, quoteId]);

  useEffect(() => {
    if (quoteData) {
      fetchStatusHistory();
      calculateExpiryDays();
      fetchAccessStats();
    }
  }, [quoteData]);

  const fetchAccessStats = async () => {
    try {
      setLoadingStats(true);
      const response = await axios.get(
        getApiUrl(`${companyId}/quotes/${quoteId}/access-stats`),
        { withCredentials: true }
      );

      console.log('Access stats:', response.data.data);

      setAccessStats(response.data.data);
    } catch (error) {
      console.error('Error fetching access stats:', error);
      // Don't show error for this, it's not critical
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchPdf = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/quotes/${quoteId}/pdf`),
        {
          responseType: 'blob',
          withCredentials: true,
        }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      setPdfData(URL.createObjectURL(file));
    } catch (error) {
      console.error('Error fetching PDF:', error);
      setError('Kunde inte hämta PDF-filen. Försök igen senare.');
    }
  };

  const fetchQuote = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/quotes/${quoteId}`),
        { withCredentials: true }
      );
      setQuoteData(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching quote:', error);
      setError('Kunde inte hämta offertdetaljer. Försök igen senare.');
      setLoading(false);
    }
  };

  const fetchStatusHistory = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/quotes/${quoteId}/status-history`),
        { withCredentials: true }
      );

      console.log('Status history:', response.data.data);

      setStatusHistory(response.data.data);
      determineNextAction(quoteData.status);
    } catch (error) {
      console.error('Error fetching status history:', error);
      determineNextAction(quoteData.status);
    }
  };

  const calculateExpiryDays = () => {
    // Use startOf('day') to ensure both dates are compared at start of day
    // This prevents timezone and time-of-day issues in the calculation
    const today = dayjs().startOf('day');
    const validUntil = dayjs(quoteData.valid_until).startOf('day');
    setDaysUntilExpiry(validUntil.diff(today, 'day'));
  };

  const determineNextAction = (currentStatus) => {
    switch (currentStatus) {
      case 'Draft':
        setNextAction({ text: 'Skicka offert', nextStatus: 'Sent' });
        break;
      case 'Sent':
        setNextAction({
          text: 'Markera som accepterad',
          nextStatus: 'Accepted',
          showReject: true
        });
        break;
      default:
        setNextAction(null);
    }
  };

  const getStatusDisplayName = (status) => ({
    Draft: 'Utkast',
    Sent: 'Skickad',
    Accepted: 'Accepterad',
    Rejected: 'Avböjd',
    Cancelled: 'Makulerad',
  }[status] || status);

  const getStatusConfig = (status) => {
    const configs = {
      'Draft': { color: '#6b7280', bg: '#f3f4f6', icon: faFileAlt },
      'Sent': { color: '#d97706', bg: '#fffbeb', icon: faPaperPlane },
      'Accepted': { color: '#059669', bg: '#f0fdf4', icon: faCheckCircle },
      'Rejected': { color: '#dc2626', bg: '#fef2f2', icon: faTimesCircle },
      'Cancelled': { color: '#6b7280', bg: '#f9fafb', icon: faBan }
    };
    return configs[status] || { color: '#6b7280', bg: '#f3f4f6', icon: faFileAlt };
  };

  const handleActionClick = (action) => {
    setShowConfirmModal({ action });
  };

  const handleStatusUpdate = async (newStatus) => {
    setActionLoading(true);
    try {
      await axios.put(
        getApiUrl(`${companyId}/quotes/${quoteId}/status`),
        { status: newStatus },
        { withCredentials: true }
      );

      setQuoteData(prev => ({ ...prev, status: newStatus }));
      setStatusHistory(prev => [
        ...prev,
        { status: newStatus, changed_at: new Date().toISOString() }
      ]);
      determineNextAction(newStatus);
      setSuccessMessage(`Offerten har ${newStatus === 'Accepted' ? 'accepterats' :
        newStatus === 'Sent' ? 'skickats' :
          newStatus === 'Rejected' ? 'avböjts' :
            'uppdaterats'}.`);
    } catch (error) {
      setError('Kunde inte uppdatera offertens status. Försök igen.');
    } finally {
      setActionLoading(false);
      setShowConfirmModal(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: quoteData?.currency || 'SEK'
    }).format(amount);
  };

  const statusConfig = quoteData ? getStatusConfig(quoteData.status) : {};

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar offertdetaljer...</span>
        </Spinner>
      </div>
    );
  }

  if (error && !quoteData) {
    return (
      <div className="quote-detail-container">
        <Alert variant="danger" className="text-center">
          <h5>Ett fel uppstod</h5>
          <p>{error}</p>
          <Link
            to={`/${companyId}/quotes`}
            className="btn btn-outline-danger btn-modern"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Tillbaka till offertlistan
          </Link>
        </Alert>
      </div>
    );
  }

  // If quoteData is still null, show loading (data is being fetched)
  if (!quoteData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar offertdetaljer...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .quote-detail-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        
        .quote-header {
          padding: 1rem 0;
          margin-bottom: 2rem;
        }

        .quote-number {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #1f2937;
          line-height: 1;
        }

        .quote-subtitle {
          color: #6b7280;
          font-size: 1.125rem;
          margin-bottom: 1.5rem;
          font-weight: 500;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.8125rem;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          border: 1px solid currentColor;
          margin-bottom: 1rem;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .stat-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        
        .stat-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        }
        
        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.25rem;
        }
        
        .stat-value {
          font-size: 1.75rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .stat-label {
          color: #6b7280;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .info-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }

        .info-card:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        }
        
        .info-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .info-card-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .info-card-icon {
          width: 20px;
          height: 20px;
          color: #6b7280;
        }
        
        .action-buttons {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .btn-group .btn-modern {
          border-radius: 0;
        }

        .btn-group .btn-modern:first-child {
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          border-right: none;
        }

        .btn-group .btn-modern:last-child {
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          border-left: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-group .btn-modern:hover {
          transform: none;
        }

        .btn-group:hover .btn-modern {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        }

        .dropdown-toggle::after {
          margin-left: 0;
        }
        
        .btn-modern {
          border-radius: 8px;
          padding: 0.75rem 1.25rem;
          font-weight: 600;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
          border: 1px solid currentColor;
        }
        
        .btn-modern:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        }

        .btn-success-modern {
          background: var(--bs-primary);
          color: white;
          border-color: var(--bs-primary);
        }

        .btn-success-modern:hover {
          background: #047857;
          border-color: #047857;
          color: white;
        }

        .customer-info-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f9fafb;
        }

        .customer-info-item:last-child {
          border-bottom: none;
        }

        .customer-info-icon {
          width: 16px;
          height: 16px;
          color: #6b7280;
          flex-shrink: 0;
        }

        .customer-info-content {
          flex: 1;
          color: #374151;
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .customer-name {
          font-weight: 600;
          font-size: 1rem;
          color: #1f2937;
          margin-bottom: 0.25rem;
        }

        .timeline-container {
          max-height: 400px;
          overflow-y: auto;
          padding-right: 0.5rem;
        }

        .timeline-container::-webkit-scrollbar {
          width: 4px;
        }

        .timeline-container::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 2px;
        }

        .timeline-container::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;
        }

        .timeline-container::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        .pdf-preview-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }

        .pdf-preview-card:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        }

        .pdf-preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .pdf-preview-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .modern-timeline {
          position: relative;
          padding-left: 2rem;
        }

        .modern-timeline::before {
          content: '';
          position: absolute;
          left: 0.75rem;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #e5e7eb;
        }

        .timeline-item {
          position: relative;
          margin-bottom: 1.5rem;
        }

        .timeline-item::before {
          content: '';
          position: absolute;
          left: -1.5rem;
          top: 0.375rem;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #059669;
          border: 3px solid white;
          box-shadow: 0 0 0 2px #e5e7eb;
        }

        .timeline-content {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .timeline-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.25rem;
        }

        .timeline-status {
          font-weight: 600;
          color: #1f2937;
        }

        .timeline-date {
          color: #6b7280;
          font-size: 0.875rem;
        }
        
        @media (max-width: 768px) {
          .quote-header {
            padding: 1.5rem;
          }
          
          .quote-number {
            font-size: 2rem;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .action-buttons {
            flex-direction: column;
          }
          
          .btn-modern {
            justify-content: center;
          }

          .info-card {
            padding: 1rem;
          }
        }
      `}</style>

      <PageTitle activeMenu="Offertdetaljer" motherMenu="Offerter" />

      <div className="quote-detail-container">
        {/* Alerts */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
            {successMessage}
          </Alert>
        )}

        {/* Back Button - Top Left */}
        <div className="mb-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none d-flex align-items-center gap-2"
            onClick={() => history.push(`/${companyId}/quotes`)}
            style={{ color: '#64748b', fontSize: '0.875rem' }}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="sm" />
            Tillbaka till listan
          </Button>
        </div>

        {/* Enhanced Header with Customer Info */}
        <div className="quote-header">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="mb-2" style={{ fontWeight: '700' }}>
                Offert {quoteData.quote_number || quoteData.friendly_id || quoteData.id}
              </h2>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2 text-muted">
                  <FontAwesomeIcon icon={faUser} size="sm" />
                  <span style={{ fontWeight: '500' }}>
                    {quoteData.customer_name || quoteData.customer?.name || 'Okänd kund'}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{
                  padding: '0.25rem 0.75rem',
                  background: '#f8fafc',
                  borderRadius: '6px',
                  fontSize: '0.8125rem'
                }}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="sm" className="text-muted" />
                  <span className="text-muted">Offertdatum:</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>
                    {dayjs(quoteData.quote_date).format('DD MMM YYYY')}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{
                  padding: '0.25rem 0.75rem',
                  background: daysUntilExpiry < 0 ? '#fef2f2' : daysUntilExpiry <= 7 ? '#fffbeb' : '#f0fdf4',
                  borderRadius: '6px',
                  fontSize: '0.8125rem'
                }}>
                  <FontAwesomeIcon
                    icon={faHourglassHalf}
                    size="sm"
                    style={{
                      color: daysUntilExpiry < 0 ? '#ef4444' : daysUntilExpiry <= 7 ? '#f59e0b' : '#10b981'
                    }}
                  />
                  <span className="text-muted">Giltig till:</span>
                  <span style={{
                    fontWeight: '600',
                    color: daysUntilExpiry < 0 ? '#ef4444' : daysUntilExpiry <= 7 ? '#f59e0b' : '#10b981'
                  }}>
                    {dayjs(quoteData.valid_until).format('DD MMM YYYY')}
                  </span>
                  {daysUntilExpiry < 0 && (
                    <Badge bg="danger" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                      Utgången
                    </Badge>
                  )}
                  {daysUntilExpiry >= 0 && daysUntilExpiry <= 7 && (
                    <Badge bg="warning" text="dark" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                      {daysUntilExpiry}d kvar
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Badge
                style={{
                  color: statusConfig.color,
                  backgroundColor: statusConfig.bg,
                  borderColor: statusConfig.color + '40',
                  border: `1px solid`,
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                <FontAwesomeIcon icon={statusConfig.icon} className="me-2" />
                {getStatusDisplayName(quoteData.status)}
              </Badge>
              <div className="action-buttons">
                {nextAction && (
                  <Dropdown as={ButtonGroup}>
                    <Button
                      className="btn btn-success-modern btn-modern"
                      onClick={() => handleActionClick(nextAction.nextStatus)}
                      disabled={actionLoading}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {actionLoading ? 'Uppdaterar...' : nextAction.text}
                    </Button>

                    <Dropdown.Toggle
                      split
                      className="btn btn-success-modern btn-modern"
                      id="quote-actions-dropdown"
                      disabled={actionLoading}
                    />

                    <Dropdown.Menu align="end">
                      {nextAction.showReject && (
                        <>
                          <Dropdown.Item
                            onClick={() => handleActionClick('Rejected')}
                            className="text-danger"
                          >
                            <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                            Avböj offert
                          </Dropdown.Item>
                          <Dropdown.Divider />
                        </>
                      )}

                      <Dropdown.Item
                        as={Link}
                        to={`/${companyId}/edit-quote/${quoteId}`}
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        Redigera offert
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => window.open(pdfData, '_blank')}
                        disabled={!pdfData}
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Ladda ner PDF
                      </Dropdown.Item>

                      <Dropdown.Divider />

                      <Dropdown.Item>
                        <FontAwesomeIcon icon={faCopy} className="me-2" />
                        Duplicera offert
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                {!nextAction && (
                  <Dropdown>
                    <Dropdown.Toggle
                      className="btn btn-outline-secondary btn-modern"
                      id="quote-actions-dropdown"
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                      Åtgärder
                    </Dropdown.Toggle>

                    <Dropdown.Menu align="end">
                      <Dropdown.Item
                        as={Link}
                        to={`/${companyId}/edit-quote/${quoteId}`}
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        Redigera offert
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => window.open(pdfData, '_blank')}
                        disabled={!pdfData}
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Ladda ner PDF
                      </Dropdown.Item>

                      <Dropdown.Divider />

                      <Dropdown.Item>
                        <FontAwesomeIcon icon={faCopy} className="me-2" />
                        Duplicera offert
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
              <FontAwesomeIcon icon={faEuroSign} />
            </div>
            <div className="stat-value" style={{ color: '#0284c7' }}>
              {formatCurrency(quoteData.totals?.grand_total || quoteData.totals?.total || 0)}
            </div>
            <div className="stat-label">Totalt belopp</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div className="stat-value" style={{ color: '#d97706' }}>
              {daysUntilExpiry > 0 ? daysUntilExpiry : 0}
            </div>
            <div className="stat-label">Dagar kvar</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
              <FontAwesomeIcon icon={faFileInvoice} />
            </div>
            <div className="stat-value" style={{ color: '#059669' }}>
              {quoteData.items?.length || 0}
            </div>
            <div className="stat-label">Antal artiklar</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#faf5ff', color: '#9333ea' }}>
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="stat-value" style={{ color: '#9333ea' }}>
              {dayjs(quoteData.created_at).format('DD MMM')}
            </div>
            <div className="stat-label">Skapad</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
              <FontAwesomeIcon icon={faEye} />
            </div>
            <div className="stat-value" style={{ color: '#d97706' }}>
              {loadingStats ? (
                <Spinner size="sm" />
              ) : (
                accessStats?.total_views || 0
              )}
            </div>
            <div className="stat-label">Antal visningar</div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-4">
            <div className="d-flex flex-column gap-4">
              {/* Customer Details */}
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">
                    <FontAwesomeIcon
                      icon={quoteData.customer?.type === 'company' ? faBuilding : faUser}
                      className="info-card-icon"
                    />
                    Kunduppgifter
                  </h3>
                </div>

                <div>
                  <div className="customer-name">
                    {/* Handle both new flattened format and old nested format */}
                    {quoteData.customer_name ||
                     (quoteData.customer?.type === 'company'
                       ? quoteData.customer.company_name
                       : `${quoteData.customer?.first_name || ''} ${quoteData.customer?.last_name || ''}`.trim()) ||
                     'Okänd kund'}
                  </div>

                  <div className="d-flex flex-column gap-0">
                    {/* Email - handle both formats */}
                    {(quoteData.customer_email || quoteData.customer?.email) && (
                      <div className="customer-info-item">
                        <FontAwesomeIcon icon={faEnvelope} className="customer-info-icon" />
                        <div className="customer-info-content">
                          {quoteData.customer_email || quoteData.customer?.email}
                        </div>
                      </div>
                    )}

                    {/* Phone - handle both formats */}
                    {(quoteData.customer_phone || quoteData.customer?.phone) && (
                      <div className="customer-info-item">
                        <FontAwesomeIcon icon={faPhone} className="customer-info-icon" />
                        <div className="customer-info-content">
                          {quoteData.customer_phone || quoteData.customer?.phone}
                        </div>
                      </div>
                    )}

                    {/* Address - handle both flattened and nested formats */}
                    {(quoteData.customer_street1 || quoteData.customer?.address?.street1) && (
                      <div className="customer-info-item">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="customer-info-icon" />
                        <div className="customer-info-content">
                          <div>{quoteData.customer_street1 || quoteData.customer?.address?.street1}</div>
                          <div>
                            {quoteData.customer_postalcode || quoteData.customer?.address?.postalcode}{' '}
                            {quoteData.customer_city || quoteData.customer?.address?.city}
                          </div>
                          <div>{quoteData.customer_country || quoteData.customer?.address?.country || 'Sverige'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {accessStats && (
                <div className="info-card">
                  <div className="info-card-header">
                    <h3 className="info-card-title">
                      <FontAwesomeIcon icon={faEye} className="info-card-icon" />
                      Åtkomststatistik
                    </h3>
                  </div>

                  <div className="row g-3">
                    <div className="col-6">
                      <div className="text-center p-3" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                          {accessStats.total_views || 0}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                          Totala visningar
                        </div>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="text-center p-3" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                          {accessStats.unique_views || 0}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                          Unika visningar
                        </div>
                      </div>
                    </div>

                    {accessStats.first_viewed_at && (
                      <div className="col-12">
                        <div className="customer-info-item">
                          <FontAwesomeIcon icon={faCalendarAlt} className="customer-info-icon" />
                          <div className="customer-info-content">
                            <div style={{ fontWeight: '500', marginBottom: '0.125rem' }}>Första visning</div>
                            <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                              {dayjs(accessStats.first_viewed_at).format('DD MMMM YYYY HH:mm')}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {accessStats.last_viewed_at && (
                      <div className="col-12">
                        <div className="customer-info-item">
                          <FontAwesomeIcon icon={faClock} className="customer-info-icon" />
                          <div className="customer-info-content">
                            <div style={{ fontWeight: '500', marginBottom: '0.125rem' }}>Senaste visning</div>
                            <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                              {dayjs(accessStats.last_viewed_at).format('DD MMMM YYYY HH:mm')}
                              {' '}({dayjs(accessStats.last_viewed_at).fromNow()})
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {accessStats.total_views === 0 && (
                      <div className="col-12">
                        <div
                          className="text-center p-3"
                          style={{
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid #f59e0b20'
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: '#f59e0b', marginBottom: '0.5rem' }}
                            size="lg"
                          />
                          <div style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: '500' }}>
                            Offerten har inte visats av kunden än
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#a16207', marginTop: '0.25rem' }}>
                            Kunden kanske inte har fått länken eller har inte öppnat den
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recent Access History */}
                  {accessStats.recent_views && accessStats.recent_views.length > 0 && (
                    <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #f3f4f6' }}>
                      <h6 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
                        Senaste visningar
                      </h6>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {accessStats.recent_views.map((view, index) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{
                              borderBottom: index < accessStats.recent_views.length - 1 ? '1px solid #f9fafb' : 'none',
                              fontSize: '0.8125rem'
                            }}
                          >
                            <div style={{ color: '#374151' }}>
                              {dayjs(view.viewed_at).format('DD MMM YYYY HH:mm')}
                            </div>
                            <div style={{ color: '#6b7280' }}>
                              {view.ip_address && (
                                <span className="me-2">IP: {view.ip_address}</span>
                              )}
                              {view.user_agent && (
                                <span style={{ fontSize: '0.75rem' }}>
                                  {view.user_agent.includes('Mobile') ? '📱' : '💻'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Status Timeline */}
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">
                    <FontAwesomeIcon icon={faClock} className="info-card-icon" />
                    Statushistorik
                  </h3>
                </div>

                <div className="timeline-container">
                  <div className="modern-timeline">
                    {statusHistory && statusHistory.length > 0 ? (
                      statusHistory.map((statusEvent, index) => {
                        const config = getStatusConfig(statusEvent.status);
                        return (
                          <div key={index} className="timeline-item">
                            <div className="timeline-content">
                              <div className="timeline-header">
                                <FontAwesomeIcon
                                  icon={config.icon}
                                  style={{ color: config.color }}
                                />
                                <span className="timeline-status">
                                  {getStatusDisplayName(statusEvent.status)}
                                </span>
                              </div>
                              <div className="timeline-date">
                                {dayjs(statusEvent.changed_at).format('DD MMMM YYYY HH:mm')}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-muted text-center py-3">
                        Ingen statushistorik tillgänglig
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            {/* PDF Preview */}
            <div className="pdf-preview-card">
              <div className="pdf-preview-header">
                <h3 className="pdf-preview-title">
                  <FontAwesomeIcon icon={faFileInvoice} className="info-card-icon" />
                  Offert förhandsvisning
                </h3>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => window.open(pdfData, '_blank')}
                    disabled={!pdfData}
                  >
                    <FontAwesomeIcon icon={faDownload} className="me-1" />
                    Ladda ner
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => window.print()}
                  >
                    <FontAwesomeIcon icon={faPrint} className="me-1" />
                    Skriv ut
                  </button>
                </div>
              </div>

              {pdfData ? (
                <PDFViewer fileUrl={pdfData} />
              ) : (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Laddar PDF förhandsvisning...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Bekräfta åtgärd</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {showConfirmModal?.action === 'Rejected' ? (
              'Är du säker på att du vill avböja denna offert?'
            ) : (
              `Är du säker på att du vill ${nextAction?.text.toLowerCase()}?`
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              Avbryt
            </Button>
            <Button
              variant={showConfirmModal?.action === 'Rejected' ? 'danger' : 'success'}
              onClick={() => handleStatusUpdate(showConfirmModal.action)}
              disabled={actionLoading}
            >
              {actionLoading ? 'Uppdaterar...' : 'Ja, fortsätt'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default QuoteViewer;




// import React, { useEffect, useState } from 'react';
// import { useParams, Link } from 'react-router-dom';
// import axios from '../../../../services/AxiosInstance';
// import PDFViewer from './PdfViewer';
// import dayjs from 'dayjs';
// import {
//   Spinner,
//   Card,
//   Button,
//   Badge,
//   Alert,
//   Modal,
//   Row,
// } from 'react-bootstrap';

// import { getApiUrl } from '../../../../utils/ApiUtils';
// import PageTitle from '../../../layouts/PageTitle';

// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import {
//   faFileAlt,       // Draft
//   faPaperPlane,    // Sent
//   faCheckCircle,   // Accepted
//   faTimesCircle,   // Rejected
//   faBan,           // Cancelled
// } from '@fortawesome/free-solid-svg-icons';

// const QuoteViewer = () => {
//   const { companyId, quoteId } = useParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState(null);

//   // State for quote data
//   const [quoteData, setQuoteData] = useState(null);
//   const [pdfData, setPdfData] = useState(null);
//   const [daysUntilExpiry, setDaysUntilExpiry] = useState(null);
//   const [statusHistory, setStatusHistory] = useState([]);
//   const [nextAction, setNextAction] = useState(null);

//   // Modal state
//   const [showConfirmModal, setShowConfirmModal] = useState(false);
//   const [actionLoading, setActionLoading] = useState(false);

//   useEffect(() => {
//     fetchQuote();
//     fetchPdf();
//   }, [companyId, quoteId]);

//   useEffect(() => {
//     if (quoteData) {
//       fetchStatusHistory();
//       calculateExpiryDays();
//     }
//   }, [quoteData]);

//   const fetchPdf = async () => {
//     try {
//       const response = await axios.get(
//         getApiUrl(`${companyId}/quotes/${quoteId}/pdf`),
//         {
//           responseType: 'blob',
//           withCredentials: true,
//         }
//       );
//       const file = new Blob([response.data], { type: 'application/pdf' });
//       setPdfData(URL.createObjectURL(file));
//     } catch (error) {
//       console.error('Error fetching PDF:', error);
//       setError('Kunde inte hämta PDF-filen. Försök igen senare.');
//     }
//   };

//   const fetchQuote = async () => {
//     try {
//       const response = await axios.get(
//         getApiUrl(`${companyId}/quotes/${quoteId}`),
//         { withCredentials: true }
//       );
//       setQuoteData(response.data.quote);
//       setLoading(false);
//     } catch (error) {
//       console.error('Error fetching quote:', error);
//       setError('Kunde inte hämta offertdetaljer. Försök igen senare.');
//       setLoading(false);
//     }
//   };

//   const fetchStatusHistory = async () => {
//     try {
//       const response = await axios.get(
//         getApiUrl(`${companyId}/quotes/${quoteId}/status-history`),
//         { withCredentials: true }
//       );

//       console.log('Status history:', response.data.history);

//       setStatusHistory(response.data.history);
//       determineNextAction(quoteData.status);
//     } catch (error) {
//       console.error('Error fetching status history:', error);
//       determineNextAction(quoteData.status);
//     }
//   };

//   const calculateExpiryDays = () => {
//     const today = dayjs();
//     const validUntil = dayjs(quoteData.valid_until);
//     setDaysUntilExpiry(validUntil.diff(today, 'day'));
//   };

//   const determineNextAction = (currentStatus) => {
//     switch (currentStatus) {
//       case 'Draft':
//         setNextAction({ text: 'Skicka offert', nextStatus: 'Sent' });
//         break;
//       case 'Sent':
//         setNextAction({
//           text: 'Markera som accepterad',
//           nextStatus: 'Accepted',
//           showReject: true
//         });
//         break;
//       default:
//         setNextAction(null);
//     }
//   };

//   const getStatusDisplayName = (status) => ({
//     Draft: 'Utkast',
//     Sent: 'Skickad',
//     Accepted: 'Accepterad',
//     Rejected: 'Avböjd',
//     Cancelled: 'Makulerad',
//   }[status] || status);

//   const getStatusIcon = (status) => ({
//     Draft: { icon: faFileAlt, color: '#6c757d' },
//     Sent: { icon: faPaperPlane, color: '#0d6efd' },
//     Accepted: { icon: faCheckCircle, color: '#198754' },
//     Rejected: { icon: faTimesCircle, color: '#dc3545' },
//     Cancelled: { icon: faBan, color: '#6c757d' },
//   }[status] || { icon: faFileAlt, color: '#6c757d' });

//   const handleActionClick = (action) => {
//     setShowConfirmModal({ action });
//   };

//   const handleStatusUpdate = async (newStatus) => {
//     setActionLoading(true);
//     try {
//       await axios.put(
//         getApiUrl(`${companyId}/quotes/${quoteId}/status`),
//         { status: newStatus },
//         { withCredentials: true }
//       );

//       setQuoteData(prev => ({ ...prev, status: newStatus }));
//       setStatusHistory(prev => [
//         ...prev,
//         { status: newStatus, changed_at: new Date().toISOString() }
//       ]);
//       determineNextAction(newStatus);
//       setSuccessMessage(`Offerten har ${newStatus === 'Accepted' ? 'accepterats' :
//         newStatus === 'Sent' ? 'skickats' :
//           newStatus === 'Rejected' ? 'avböjts' :
//             'uppdaterats'}.`);
//     } catch (error) {
//       setError('Kunde inte uppdatera offertens status. Försök igen.');
//     } finally {
//       setActionLoading(false);
//       setShowConfirmModal(false);
//     }
//   };

//   return (
//     <>
//       <PageTitle activeMenu="Offerter" motherMenu="Offerter" />
//       {loading ? (
//         <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
//           <Spinner animation="border" role="status">
//             <span className="visually-hidden">Laddar...</span>
//           </Spinner>
//         </div>
//       ) : (
//         <div className="col-xl-12 mt-4">
//           {/* Alerts */}
//           {error && (
//             <Alert variant="danger" onClose={() => setError(null)} dismissible>
//               {error}
//             </Alert>
//           )}
//           {successMessage && (
//             <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
//               {successMessage}
//             </Alert>
//           )}

//           {/* Header with Actions */}
//           <div className='col-xl-12 mb-5' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center-horizontal' }}>
//             <h2>Offertdetaljer</h2>
//             <div className="d-flex gap-2">
//               {nextAction && (
//                 <>
//                   <Button
//                     variant="primary"
//                     onClick={() => handleActionClick(nextAction.nextStatus)}
//                     disabled={actionLoading}
//                   >
//                     {actionLoading ? 'Uppdaterar...' : nextAction.text}
//                   </Button>
//                   {nextAction.showReject && (
//                     <Button
//                       variant="danger"
//                       onClick={() => handleActionClick('Rejected')}
//                       disabled={actionLoading}
//                     >
//                       Avböj offert
//                     </Button>
//                   )}
//                 </>
//               )}
//             </div>
//           </div>

//           {/* Status Timeline */}
//           <div className="col-xl-12 col-xxl-12 col-lg-12 mb-4 widget-timeline">
//             <ul className="timeline">
//               {statusHistory && statusHistory.length > 0 ? (

//                 statusHistory.map((statusEvent, index) => {
//                   const { icon, color } = getStatusIcon(statusEvent.status);
//                   return (
//                     <li key={index}>
//                       <div className="timeline-badge primary" ></div>
//                       <div className="timeline-panel text-muted">
//                         <span>{dayjs(statusEvent.changed_at).format('YYYY-MM-DD HH:mm')}</span>
//                         <h6 className="mb-0 d-flex align-items-center">
//                           <FontAwesomeIcon icon={icon} color={color} size="lg" />
//                           <span className="ms-2">{getStatusDisplayName(statusEvent.status)}</span>
//                         </h6>
//                       </div>
//                     </li>
//                   );
//                 })
//               ) : (
//                 <li>
//                   <div className="timeline-badge"></div>
//                   <div className="timeline-panel text-muted">
//                     <span>Ingen statushistorik tillgänglig</span>
//                   </div>
//                 </li>
//               )
//               }


//             </ul>
//           </div>

//           {/* Quote Details Card */}
//           <Card className="mt-4 mb-4">
//             <Card.Body>
//               <Row>
//                 <div className="accordion_body">
//                   <div style={{ display: "flex", flexFlow: "wrap", justifyContent: "space-between" }}>
//                     <div className="me-3 mb-3">
//                       <p className="fs-12 mb-2">Kund</p>
//                       <span className="font-w500">
//                         <strong>{quoteData.customer_name}</strong>
//                       </span>
//                     </div>
//                     <div className="me-3 mb-3">
//                       <p className="fs-12 mb-2">Offertnr.</p>
//                       <span className="font-w500">
//                         <strong>{quoteData.friendly_id}</strong>
//                       </span>
//                     </div>
//                     <div className="me-3 mb-3">
//                       <p className="fs-12 mb-2">Offertdatum</p>
//                       <span className="font-w500">
//                         <strong>{dayjs(quoteData.quote_date).format('DD MMM YYYY')}</strong>
//                       </span>
//                     </div>
//                     <div className="me-3 mb-3">
//                       <p className="fs-12 mb-2">Giltig till</p>
//                       <span className="font-w500">
//                         <strong>
//                           {dayjs(quoteData.valid_until).format('DD MMM YYYY')}
//                           {daysUntilExpiry > 0 && ` (${daysUntilExpiry} dagar kvar)`}
//                         </strong>
//                       </span>
//                     </div>
//                     <div className="me-3 mb-3">
//                       <p className="fs-12 mb-2">Status</p>
//                       <span className="font-w500">
//                         <Badge bg="primary">{getStatusDisplayName(quoteData.status)}</Badge>
//                       </span>
//                     </div>
//                     <div className="me-3 mb-3">
//                       <p className="fs-12 mb-2">Valuta</p>
//                       <span className="font-w500">
//                         <strong>{quoteData.currency}</strong>
//                       </span>
//                     </div>
//                     <div className="me-3 mb-3">
//                       <p className="fs-12 mb-2">Summa</p>
//                       <span className="font-w500">
//                         <strong>
//                           {new Intl.NumberFormat('sv-SE', {
//                             style: 'currency',
//                             currency: quoteData.currency || 'SEK',
//                           }).format(quoteData.totals.grand_total)}
//                         </strong>
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </Row>
//             </Card.Body>
//           </Card>

//           {/* PDF Viewer */}
//           {pdfData && <PDFViewer fileUrl={pdfData} />}

//           {/* Confirmation Modal */}
//           <Modal
//             show={showConfirmModal}
//             onHide={() => setShowConfirmModal(false)}
//           >
//             <Modal.Header closeButton>
//               <Modal.Title>Bekräfta åtgärd</Modal.Title>
//             </Modal.Header>
//             <Modal.Body>
//               {showConfirmModal?.action === 'Rejected' ? (
//                 'Är du säker på att du vill avböja denna offert?'
//               ) : (
//                 `Är du säker på att du vill ${nextAction?.text.toLowerCase()}?`
//               )}
//             </Modal.Body>
//             <Modal.Footer>
//               <Button
//                 variant="secondary"
//                 onClick={() => setShowConfirmModal(false)}
//               >
//                 Avbryt
//               </Button>
//               <Button
//                 variant={showConfirmModal?.action === 'Rejected' ? 'danger' : 'success'}
//                 onClick={() => handleStatusUpdate(showConfirmModal.action)}
//                 disabled={actionLoading}
//               >
//                 {actionLoading ? 'Uppdaterar...' : 'Ja, fortsätt'}
//               </Button>
//             </Modal.Footer>
//           </Modal>
//         </div>
//       )}
//     </>
//   );
// };

// export default QuoteViewer;